import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { Video, Volume2, VolumeX, ArrowRight } from 'lucide-react';
import type { StoreClip } from '@/services/creatorApi';

// Short captioned 9:16 cuts from the creator's replays (Joe, Oct 11 2026): the
// store shows the person, not only posters. A card plays muted and looped once
// it is mostly on screen (captions are burned in); a tap brings the sound up and
// silences the others. Each card links to the replay it came from.

function ClipCard({ clip, href, active, onActivate }: { clip: StoreClip; href: string; active: boolean; onActivate: (id: string | null) => void }) {
  const ref = useRef<HTMLVideoElement>(null);

  useEffect(() => {
    const v = ref.current;
    if (!v || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(([e]) => {
      if (e.isIntersecting && e.intersectionRatio >= 0.6) v.play().catch(() => {});
      else { v.pause(); if (active) onActivate(null); }
    }, { threshold: [0, 0.6, 1] });
    io.observe(v);
    return () => io.disconnect();
  }, [active, onActivate]);

  useEffect(() => { if (ref.current) ref.current.muted = !active; }, [active]);

  const toggle = () => {
    const v = ref.current;
    if (!v) return;
    if (active) { onActivate(null); return; }
    v.currentTime = 0;
    v.play().catch(() => {});
    onActivate(clip._id);
  };

  return (
    <div style={{ flex: '0 0 auto', width: 'clamp(150px, 42vw, 200px)', scrollSnapAlign: 'start' }}>
      <button
        type="button"
        onClick={toggle}
        aria-label={active ? `Mute ${clip.title}` : `Play ${clip.title} with sound`}
        style={{ position: 'relative', display: 'block', width: '100%', aspectRatio: '9 / 16', borderRadius: 'var(--hj-r-md)', overflow: 'hidden', background: '#0A0A0A', border: 0, padding: 0, cursor: 'pointer' }}
      >
        <video
          ref={ref}
          src={clip.videoUrl}
          poster={clip.posterUrl || undefined}
          muted
          loop
          playsInline
          preload="metadata"
          style={{ width: '100%', height: '100%', objectFit: 'cover', display: 'block' }}
        />
        <span
          style={{ position: 'absolute', right: 8, bottom: 8, width: 32, height: 32, borderRadius: 999, background: 'rgba(10,10,10,0.72)', color: '#fff', display: 'inline-flex', alignItems: 'center', justifyContent: 'center' }}
        >
          {active ? <Volume2 size={16} /> : <VolumeX size={16} />}
        </span>
      </button>
      <div style={{ marginTop: 8, fontSize: 13.5, fontWeight: 700, lineHeight: 1.3, color: 'var(--hj-ink)', display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
        {clip.title}
      </div>
      {clip.productId && (
        <Link to={href} className="inline-flex items-center gap-1" style={{ marginTop: 4, fontSize: 12.5, fontWeight: 600, color: 'var(--hj-ink-2)', textDecoration: 'underline', textUnderlineOffset: 3 }}>
          Watch the full replay <ArrowRight size={12} />
        </Link>
      )}
    </div>
  );
}

export function StoreClipsRow({ clips, name, courseHref }: { clips: StoreClip[]; name: string; courseHref: (id: string) => string }) {
  const [active, setActive] = useState<string | null>(null);
  if (!clips.length) return null;
  return (
    <section style={{ marginTop: 24 }}>
      <div className="flex items-baseline gap-2" style={{ marginBottom: 12 }}>
        <Video size={14} style={{ color: 'var(--hj-ink-2)', alignSelf: 'center' }} />
        <h2 className="font-hj-display m-0" style={{ fontSize: 'clamp(18px, 2.4vw, 22px)', fontWeight: 800, letterSpacing: '-0.01em', color: 'var(--hj-ink)' }}>
          Clips from {name}'s sessions
        </h2>
      </div>
      <div
        className="flex gap-3"
        style={{ overflowX: 'auto', scrollSnapType: 'x mandatory', paddingBottom: 6, WebkitOverflowScrolling: 'touch' }}
      >
        {clips.map(c => (
          <ClipCard key={c._id} clip={c} href={courseHref(c.productId)} active={active === c._id} onActivate={setActive} />
        ))}
      </div>
    </section>
  );
}
